import * as React from 'react';
import { Provider, useSelector } from 'react-redux';

import type { AiCoach_Action as AiCoachAction, AiCoach_Mode as AiCoachMode } from '__generated__/graphql-types';

import { useRetracked } from 'js/lib/retracked';
import { get as getUser } from 'js/lib/user';

import { Typography2 } from '@coursera/cds-core';
import type { CourseItem, Product } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { CoachButton, CoachDisclosure, CoachTriggerButton } from 'bundles/ai-course-coach/components/common';
import type { CoachDisclosureProps } from 'bundles/ai-course-coach/components/common';
import store from 'bundles/ai-course-coach/store';
import { openCoachWidget, sendMessage } from 'bundles/ai-course-coach/store/dispatcher';
import type { GlobalState } from 'bundles/ai-course-coach/store/types';
import localStorageEx from 'bundles/common/utils/localStorageEx';
import { useCourseToolBarPlugins } from 'bundles/course-v2/components/navigation/CourseToolBar/useCourseToolBarPlugins';

export type ExternalCoachDisclosureProps = {
  courseId?: string;
  itemId?: string;
  disclosureMessage: React.ReactNode;
  actionButtons: {
    label: string;
    onClick?: (e?: React.MouseEvent) => void;
    isCoachTrigger?: boolean;
    mode: AiCoachMode;
    action: AiCoachAction;
  }[];
  recommendedActionsEnabled?: boolean;
  clipRecommendationsEnabled?: boolean;
  stickyPreferenceKey?: string;
} & Omit<CoachDisclosureProps, 'children'>;

type StoreProviderWrapperProps = {
  children: JSX.Element;
};

const StoreProviderWrapper: React.FC<StoreProviderWrapperProps> = ({ children }) => {
  return <Provider store={store}>{children}</Provider>;
};

export const ExternalCoachDisclosureContent = (props: ExternalCoachDisclosureProps): React.ReactElement | null => {
  const {
    disclosureMessage,
    actionButtons,
    courseId,
    itemId,
    recommendedActionsEnabled = false,
    clipRecommendationsEnabled = false,
  } = props;

  const sharedEventingData = {
    courseItem: {
      id: itemId as string,
    } as CourseItem,
    product: {
      type: 'course',
      id: courseId as string,
    } as Product,
  };

  const { isMessageLoading } = useSelector((state: GlobalState) => ({
    isMessageLoading: state.behavior.messageLoader,
  }));

  const trackRetracked = useRetracked();
  const track = useTracker();
  const disclosureMode = actionButtons[0] ? actionButtons[0].mode : 'basic';

  const disclosureVisibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_coach',
    {
      coachViewType: 'view_coach_disclosure',
      coach: {
        mode: disclosureMode,
      },
      ...(courseId && { product: sharedEventingData.product }),
      ...(itemId && { courseItem: sharedEventingData.courseItem }),
    }
  );

  const { id } = getUser();
  const userId = String(id);
  const { setToolSelected, setExpanded, expanded: toolbarExpanded } = useCourseToolBarPlugins();

  const wrappedActionButtons = actionButtons?.map((actionButton) => {
    const { isCoachTrigger, label, onClick, mode, action } = actionButton;

    return isCoachTrigger ? (
      <CoachTriggerButton
        disabled={isMessageLoading}
        onClick={(e?: React.MouseEvent<HTMLButtonElement>) => {
          if (onClick) {
            onClick(e);
          }

          setToolSelected('coach');
          setExpanded(true);
          openCoachWidget();
          sendMessage({
            input: {
              userId,
              message: label,
              courseraContext: {
                courseId,
                itemId,
              },
              mode,
              action,
            },
            recommendedActionsEnabled,
            clipRecommendationsEnabled,
          });
          trackRetracked({
            trackingData: {
              input_text: label,
              timestamp: Date.now(),
            },
            trackingName: 'ai_course_coach_disclosure',
            action: `click_send_message`,
          });

          // v3 tracking
          track('interact_coach', {
            coachInteractionType: 'click_action',
            coach: {
              mode,
              location: 'disclosure',
            },
            ...(courseId && { product: sharedEventingData.product }),
            ...(itemId && { courseItem: sharedEventingData.courseItem }),
            coachAction: action,
          });
        }}
      >
        {label}
      </CoachTriggerButton>
    ) : (
      <CoachButton onClick={onClick}>{label}</CoachButton>
    );
  });

  const expandedPreferenceKey = `externalCoachDisclosureExpanded_${props.stickyPreferenceKey}`;

  function setState(expanded: boolean) {
    localStorageEx.setItem(expandedPreferenceKey, expanded, (values) => (values ? 'true' : 'false'));
  }

  function getState() {
    return localStorageEx.getItem(expandedPreferenceKey, (value) => value !== 'false', true);
  }

  const handleToggle = (expanded: boolean) => {
    if (props.stickyPreferenceKey) {
      setState(expanded);
    }

    track('interact_coach', {
      coachInteractionType: expanded ? 'coach_disclosure_expanded' : 'coach_disclosure_collapsed',
      coach: {
        // comply with lower_kebab_case Mode in schema
        // thread: https://coursera.slack.com/archives/C04U3C5T6GM/p1708038870481259
        mode: disclosureMode,
        location: 'disclosure',
      },
      ...(courseId && { product: sharedEventingData.product }),
      ...(itemId && { courseItem: sharedEventingData.courseItem }),
    });
  };

  return (
    <CoachDisclosure
      {...props}
      actionButtons={wrappedActionButtons}
      ref={disclosureVisibilityTrackingRef}
      onToggle={handleToggle}
      defaultExpanded={!toolbarExpanded && (props.stickyPreferenceKey ? getState() : undefined)}
    >
      <Typography2 component="p" variant="bodyPrimary">
        {disclosureMessage}
      </Typography2>
    </CoachDisclosure>
  );
};

export const ExternalCoachDisclosure = (props: ExternalCoachDisclosureProps): React.ReactElement => {
  return (
    <StoreProviderWrapper>
      <ExternalCoachDisclosureContent {...props} />
    </StoreProviderWrapper>
  );
};

export default ExternalCoachDisclosure;
